/**
 * A rated draft, shown with the mail it was answering.
 *
 * Why the pair is here at all: "wrong tone" or "too formal" cannot be acted on without the message
 * that provoked it. A thumbs-down plus the candidate's mail plus our text is a complete bug report —
 * you can see whether the classifier misread it, the template was wrong for the situation, or the
 * model invented a promise. A thumbs-up pair is the other half: the record of what a good reply looks
 * like, which is what the templates and the prompt get improved from.
 *
 * Two rules this file must keep:
 *
 *  - **Both texts are rendered as TEXT, never markup.** `candidateText` is a candidate's own email
 *    and is untrusted input; the same rule the request cards follow (see CLAUDE.md). No
 *    `dangerouslySetInnerHTML`, ever, however tempting formatting looks.
 *  - **This page is permission-gated and stays that way.** `candidateText` is candidate email
 *    content, so the route serving it enforces the usage permission even when RBAC is in audit-only
 *    mode. Nothing here may be copied onto a page with a weaker gate.
 */
import { useState } from 'react';
import type { DraftFeedbackSample } from '../../services/usageApi';

/** `role_logistics` → `Role logistics`. Codes are for machines; this list is read. */
function humanise(code: string): string {
  const words = `${code || 'unknown'}`.replace(/_/g, ' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
}

function Pane({ label, text, empty }: { label: string; text: string; empty: string }) {
  return (
    <div className="min-w-0">
      <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-zinc-400">{label}</p>
      {text ? (
        // Text, never markup — a candidate's email is untrusted input.
        <p className="max-h-56 overflow-y-auto whitespace-pre-wrap break-words rounded-md bg-white p-2.5 text-[13px] leading-relaxed text-zinc-700 ring-1 ring-zinc-200">
          {text}
        </p>
      ) : (
        <p className="rounded-md bg-white p-2.5 text-[13px] italic text-zinc-400 ring-1 ring-zinc-200">
          {empty}
        </p>
      )}
    </div>
  );
}

export function SampleCard({ sample }: { sample: DraftFeedbackSample }) {
  const [open, setOpen] = useState(false);
  const down = sample.vote === 'down';

  return (
    <li className={`rounded-lg p-3 ${down ? 'bg-rose-50/60' : 'bg-emerald-50/50'}`}>
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1 text-xs text-zinc-500">
        <span aria-hidden="true">{down ? '👎' : '👍'}</span>
        <span className="sr-only">{down ? 'Rated down' : 'Rated useful'}</span>
        <span className="font-medium text-zinc-700">{humanise(sample.category || 'unknown')}</span>
        <span>· {sample.draftSource || 'unknown'}</span>
        {/* The surface. One tab today, which is exactly why it is labelled rather than assumed. */}
        <span>· {humanise(sample.tab)}</span>
        <span>· {new Date(sample.at).toLocaleDateString('en-GB')}</span>
      </div>

      {sample.comment && (
        <p className="mt-1 whitespace-pre-wrap break-words text-sm text-zinc-800">{sample.comment}</p>
      )}

      {(sample.candidateText || sample.draftText) && (
        <>
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="mt-2 text-xs font-medium text-blue-700 underline underline-offset-2 hover:text-blue-800"
            aria-expanded={open}
          >
            {open ? 'Hide the mail and the draft' : 'Show the mail and the draft'}
          </button>
          {open && (
            <div className="mt-2 space-y-2">
              {sample.subject && (
                <p className="text-xs text-zinc-500">
                  Subject: <span className="text-zinc-700">{sample.subject}</span>
                </p>
              )}
              <div className="grid gap-3 md:grid-cols-2">
                <Pane
                  label="What the candidate wrote"
                  text={sample.candidateText}
                  empty="Not recorded for this vote."
                />
                <Pane
                  label="What we drafted"
                  text={sample.draftText}
                  empty="No draft — the card was flagged for a human."
                />
              </div>
              {sample.truncated && (
                <p className="text-[11px] text-zinc-400">
                  Trimmed for storage — the full thread is in Gmail.
                </p>
              )}
            </div>
          )}
        </>
      )}
    </li>
  );
}

/**
 * The two lists. Down-votes first and always expanded as a group, because they are the work; the
 * up-votes sit behind a summary so a good week does not bury the complaints.
 */
export function DraftFeedbackSamples({ samples }: { samples: { down: DraftFeedbackSample[]; up: DraftFeedbackSample[] } }) {
  return (
    <div className="space-y-5">
      {samples.down.length > 0 && (
        <div>
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-zinc-500">
            Rated down — what to fix
          </p>
          <ul className="space-y-2">
            {samples.down.map((s, i) => (
              <SampleCard key={`${s.at}-down-${i}`} sample={s} />
            ))}
          </ul>
        </div>
      )}

      {samples.up.length > 0 && (
        <details>
          <summary className="cursor-pointer text-xs font-semibold uppercase tracking-wide text-zinc-500">
            Rated useful — what worked ({samples.up.length})
          </summary>
          <ul className="mt-2 space-y-2">
            {samples.up.map((s, i) => (
              <SampleCard key={`${s.at}-up-${i}`} sample={s} />
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}
